import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { signOutUser } from "../../behavior/api/auth";
import { exchangeImpersonateToken } from "../../behavior/api/impersonation";
import { storeTokens } from "../../behavior/security/auth.service";
import { LargeAlert } from "../../components/elements";
import type { LargeAlertProps } from "../../components/elements/LargeAlert";
import { PageRoute } from "../../constants";
import useReadLocalStorage from "../../helpers/hooks/useReadLocalStorage";

const Impersonation = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const shouldExecuteRef = useRef(true);
    const candidateToken = useReadLocalStorage<string>("candidate-hub-token", {
        parse: false,
    });
    const [showAlert, setShowAlert] = useState<LargeAlertProps>({
        title: t("ImpersonationPage.loading-title"),
        subtitle: t("ImpersonationPage.loading-subtitle"),
        type: "info",
    });

    const verify = useCallback(
        async (token: string) => {
            try {
                const response = await exchangeImpersonateToken(token);

                if (!response) {
                    setShowAlert({
                        title: t("AppMessages.server-error"),
                        subtitle: "",
                        type: "info",
                    });
                    return;
                } else {
                    try {
                        if (candidateToken) {
                            await signOutUser();
                        }
                    } catch {
                        // To gracefully handle error thrown due to invalid/expired existing user session
                    }

                    storeTokens(response);
                    navigate(PageRoute.DASHBOARD, { replace: true });
                    return;
                }
            } catch {
                setShowAlert({
                    title: "This link is broken",
                    subtitle: "",
                    type: "warning",
                });
            }
        },
        [candidateToken, navigate, t],
    );

    useEffect(() => {
        if (!shouldExecuteRef.current) {
            return;
        }
        shouldExecuteRef.current = false;

        const token = searchParams.get("token");
        if (token) {
            verify(token);
        } else {
            navigate(PageRoute.ROOT, { replace: true });
        }
    }, [navigate, verify, searchParams]);

    return (
        <div>
            <LargeAlert
                title={showAlert.title}
                subtitle={showAlert.subtitle}
                type={showAlert.type}
                button={{
                    label: t("General.Button_GoTo_MyHubert"),
                    to: PageRoute.ROOT,
                }}
            />
        </div>
    );
};
export default Impersonation;
